import axios from "axios";
import { APIClient } from "helpers/api_helper";

const api = new APIClient();

/** Unwrap Spring Page from admin-style list responses. */
function unwrapPage(response) {
  const payload = response?.data ?? response;
  if (payload?.content) {
    return payload;
  }
  if (payload?.data?.content) {
    return payload.data;
  }
  if (Array.isArray(payload?.content)) {
    return payload;
  }
  return {
    content: [],
    totalPages: 0,
    totalElements: 0,
    ...(payload ?? {}),
  };
}

/** Unwrap paginated winners from merchant slot endpoint (WinnerspagewithtotalDTO). */
function unwrapSlotWinnersPage(response) {
  const payload = response?.data ?? response;
  const inner = payload?.data ?? payload;

  if (inner?.winnersPage?.content || inner?.winnersPage) {
    return inner.winnersPage;
  }
  if (inner?.content) {
    return inner;
  }
  if (payload?.winnersPage) {
    return payload.winnersPage;
  }
  if (payload?.content) {
    return payload;
  }
  return {};
}

function appendWinnerFilters(params, filters = {}) {
  if (filters.search?.trim()) params.append("search", filters.search.trim());
  if (filters.gameSlotId) params.append("gameSlotId", String(filters.gameSlotId));
  if (filters.msisdn?.trim()) params.append("msisdn", filters.msisdn.trim());
  if (filters.dateFrom) params.append("dateFrom", filters.dateFrom);
  if (filters.dateTo) params.append("dateTo", filters.dateTo);
  if (filters.rank != null && filters.rank !== "") {
    params.append("rank", String(filters.rank));
  }
  if (filters.sortBy) params.append("sortBy", filters.sortBy);
  if (filters.sortDir) params.append("sortDir", filters.sortDir);
}

export async function getMerchantWinners(page = 0, size = 20, filters = {}) {
  const params = new URLSearchParams({ page: String(page), size: String(size) });
  if (filters.search?.trim()) {
    params.append("search", filters.search.trim());
  }

  if (filters.gameSlotId) {
    params.append("gameSlotId", String(filters.gameSlotId));
    try {
      const response = await api.get(`merchant/winner-rows?${params.toString()}`);
      return unwrapPage(response);
    } catch (primaryError) {
      const fallbackParams = new URLSearchParams({
        page: String(page),
        size: String(size),
      });
      if (filters.search?.trim()) {
        fallbackParams.append("search", filters.search.trim());
      }
      const response = await api.get(
        `merchant/winners/${filters.gameSlotId}?${fallbackParams.toString()}`
      );
      return unwrapSlotWinnersPage(response);
    }
  }

  appendWinnerFilters(params, filters);
  const response = await api.get(`merchant/winner-rows?${params.toString()}`);
  return unwrapPage(response);
}

export async function downloadWinnersCsv(filters = {}) {
  const params = new URLSearchParams();
  appendWinnerFilters(params, filters);
  const baseUrl = axios.defaults.baseURL || "https://api.betaplay.ng/api/v1/";
  const token = localStorage.getItem("accessToken");
  const url = `${baseUrl}merchant/winner-rows/export?${params.toString()}`;

  const response = await fetch(url, {
    method: "GET",
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });

  if (!response.ok) {
    const text = await response.text();
    let message = text || `Export failed (${response.status})`;
    try {
      const parsed = JSON.parse(text);
      message = parsed.detail || parsed.message || message;
    } catch {
      // keep text body
    }
    throw new Error(message);
  }

  const blob = await response.blob();
  if (!blob || blob.size === 0) {
    throw new Error("Export returned an empty file");
  }

  if (blob.type?.includes("json")) {
    const text = await blob.text();
    let message = text;
    try {
      const parsed = JSON.parse(text);
      message = parsed.detail || parsed.message || text;
    } catch {
      // keep raw text
    }
    throw new Error(message);
  }

  const blobUrl = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = blobUrl;
  link.download = filters.gameSlotId
    ? `winners-slot-${filters.gameSlotId}.csv`
    : "winners-report.csv";
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(blobUrl);
}

/** @deprecated flat list — use getMerchantWinners */
export async function getWinners() {
  try {
    const response = await api.get("merchant/winners", null);
    const payload = response?.data ?? response;
    return payload?.data ?? payload;
  } catch (error) {
    console.error(error);
    throw new Error("Winners could not be loaded");
  }
}
